import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '@/lib/i18n';
import { useAuth } from '@/lib/auth';
import { LayoutDashboard, Activity, MessageCircle, BookOpen, BarChart3, UserCircle, Menu, X, Globe, LogOut, HelpCircle, CalendarDays } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';

const navItems = [
  { path: '/dashboard', icon: LayoutDashboard, key: 'dashboard' },
  { path: '/tracking', icon: Activity, key: 'tracking' },
  { path: '/chat', icon: MessageCircle, key: 'chat' },
  { path: '/content', icon: BookOpen, key: 'content' },
  { path: '/progress', icon: BarChart3, key: 'progress' },
  { path: '/guide', icon: HelpCircle, key: 'guide' },
  { path: '/profile', icon: UserCircle, key: 'profile' },
];

export default function DashboardLayout({ children }) {
  const { t, language, changeLanguage } = useLanguage();
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    nav('/');
  };

  const isAdmin = user?.role === 'admin';
  const allNavItems = isAdmin
    ? [...navItems.slice(0, -1), { path: '/admin/calendar', icon: CalendarDays, key: 'admin' }, navItems[navItems.length - 1]]
    : navItems;

  return (
    <div className="min-h-screen bg-bone flex" data-testid="dashboard-layout">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex flex-col w-64 bg-white border-r border-border/40 p-6 fixed h-full" data-testid="sidebar">
        <span className="font-display text-2xl font-semibold mb-10 cursor-pointer" onClick={() => nav('/')}>
          <span className="text-terracotta">bel</span>osophie
        </span>
        <nav className="flex-1 space-y-1">
          {allNavItems.map(item => {
            const active = location.pathname === item.path;
            return (
              <button key={item.path} onClick={() => nav(item.path)} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl font-body text-sm font-medium transition-all ${active ? 'bg-terracotta/10 text-terracotta' : 'text-foreground/60 hover:bg-warmgray/50 hover:text-foreground'}`} data-testid={`sidebar-${item.key}`}>
                <item.icon size={20} />
                {t(`nav.${item.key}`)}
              </button>
            );
          })}
        </nav>
        <Separator className="my-4" />
        <div className="space-y-2">
          <button onClick={() => changeLanguage(language === 'de' ? 'en' : 'de')} className="w-full flex items-center gap-3 px-4 py-3 rounded-xl font-body text-sm text-foreground/60 hover:bg-warmgray/50 transition-all" data-testid="sidebar-language-toggle">
            <Globe size={20} />
            {language === 'de' ? 'English' : 'Deutsch'}
          </button>
          <button onClick={handleLogout} className="w-full flex items-center gap-3 px-4 py-3 rounded-xl font-body text-sm text-foreground/60 hover:bg-destructive/10 hover:text-destructive transition-all" data-testid="sidebar-logout">
            <LogOut size={20} />
            {t('nav.logout')}
          </button>
        </div>
      </aside>

      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-border/40 px-4 py-3 flex items-center justify-between" data-testid="mobile-header">
        <span className="font-display text-xl font-semibold"><span className="text-terracotta">bel</span>osophie</span>
        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="rounded-full">
                <Avatar className="h-8 w-8">
                  <AvatarImage src={user?.picture} />
                  <AvatarFallback className="bg-terracotta/10 text-terracotta text-sm font-body">{user?.name?.charAt(0)}</AvatarFallback>
                </Avatar>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => nav('/profile')}>{t('nav.profile')}</DropdownMenuItem>
              <DropdownMenuItem onClick={() => changeLanguage(language === 'de' ? 'en' : 'de')}>
                {language === 'de' ? 'English' : 'Deutsch'}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleLogout} className="text-destructive">{t('nav.logout')}</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button variant="ghost" size="icon" onClick={() => setMobileOpen(!mobileOpen)} data-testid="mobile-menu-toggle">
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </Button>
        </div>
      </div>

      {/* Mobile Nav Drawer */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-40 bg-black/20" onClick={() => setMobileOpen(false)}>
          <div className="absolute right-0 top-0 h-full w-64 bg-white p-6 shadow-xl" onClick={e => e.stopPropagation()}>
            <nav className="mt-16 space-y-1">
              {allNavItems.map(item => {
                const active = location.pathname === item.path;
                return (
                  <button key={item.path} onClick={() => { nav(item.path); setMobileOpen(false); }} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl font-body text-sm font-medium transition-all ${active ? 'bg-terracotta/10 text-terracotta' : 'text-foreground/60'}`} data-testid={`mobile-nav-${item.key}`}>
                    <item.icon size={20} />
                    {t(`nav.${item.key}`)}
                  </button>
                );
              })}
            </nav>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="flex-1 lg:ml-64 pt-16 lg:pt-0" data-testid="dashboard-main-content">
        <div className="p-6 md:p-8 lg:p-12 max-w-6xl">
          {children}
        </div>
      </main>

      {/* Mobile Bottom Tabs */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white/90 backdrop-blur-md border-t border-border/40 px-2 py-2 flex justify-around z-50" data-testid="mobile-bottom-tabs">
        {navItems.slice(0, 5).map(item => {
          const active = location.pathname === item.path;
          return (
            <button key={item.path} onClick={() => nav(item.path)} className={`flex flex-col items-center gap-0.5 p-2 rounded-lg transition-colors ${active ? 'text-terracotta' : 'text-foreground/40'}`} data-testid={`tab-${item.key}`}>
              <item.icon size={20} />
              <span className="font-body text-[10px] font-medium">{t(`nav.${item.key}`)}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
